import * as d3 from 'd3'
import {
  Bounds,
  Data,
  GraphLayoutKind,
  Layout,
  NodeData,
  NodeShape,
} from '../../types'
import { layoutLinkedCircles } from '../layoutLinkedCircles'

// Linked layout (#107): the linked circles and network views. The placement
// itself lives in layoutLinkedCircles; this adapter gives its nodes the shared
// geometry, so culling, drag & drop, zoom, minimap and export work on them as
// on the packed circles.
//
// Every disc and every crown role is a direct child of the artificial root, and
// a disc's own roles are packed inside it: each node's subtree fits in its own
// circle, so its bounds are its own box, as in a packing.
export function computeLinkedLayout(
  data: Data,
  packSorting: (a: d3.HierarchyNode<Data>, b: d3.HierarchyNode<Data>) => number,
  rolesAround = false
): Layout {
  const { root, nodes, links, linkLegend } = layoutLinkedCircles(
    data,
    packSorting,
    { rolesAround }
  )

  for (const node of [root, ...nodes]) setCircleGeometry(node)

  // The root circle frames the discs, but a crown of roles (network view) or
  // a clasp can reach past it: the layout box covers them all.
  const bounds = nodes.reduce<Bounds>(
    (box, node) => ({
      x0: Math.min(box.x0, node.bounds.x0),
      y0: Math.min(box.y0, node.bounds.y0),
      x1: Math.max(box.x1, node.bounds.x1),
      y1: Math.max(box.y1, node.bounds.y1),
    }),
    root.bounds
  )

  return {
    root,
    nodes,
    kind: GraphLayoutKind.Linked,
    bounds,
    focus: { x: root.x, y: root.y, r: root.r },
    focusBox: bounds,
    links: [],
    discLinks: links,
    linkLegend,
  }
}

function setCircleGeometry(node: NodeData) {
  node.w = node.r * 2
  node.h = node.r * 2
  node.shape = NodeShape.Circle
  node.bounds = {
    x0: node.x - node.r,
    y0: node.y - node.r,
    x1: node.x + node.r,
    y1: node.y + node.r,
  }
}
